import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button, PermissionsProvider} from '@/components';
import {TableSkeleton} from '@/components/Table/TableSkeleton';
import type {AdminBusinessAffiliateTier} from '@/types';
import {logRejection} from '@/utils/logRejection';

import AffiliateTierFormModal from './AffiliateTierFormModal';
import AffiliateTierTable from './AffiliateTierTable';
import ConfirmTierChangeModal from './ConfirmTierChangeModal';
import useAffiliateTiers from './useAffiliateTiers';

export const AffiliateTiersTab = () => {
  const {
    tiers,
    isLoading,
    isSaving,
    errors,
    error,
    validateCreate,
    validateUpdate,
    handleCreate,
    handleUpdate,
    makeDefault,
    clearErrors,
  } = useAffiliateTiers();

  const [editing, setEditing] = useState<AdminBusinessAffiliateTier>();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [makingDefault, setMakingDefault] =
    useState<AdminBusinessAffiliateTier>();

  const currentDefault = tiers.find((t) => t.isDefault && !t.isDeactivated);

  const openForm = (tier?: AdminBusinessAffiliateTier) => {
    clearErrors();
    setEditing(tier);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditing(undefined);
  };

  const submitCreate = async (
    form: Parameters<typeof handleCreate>[0],
  ): Promise<boolean> => {
    const saved = await handleCreate(form);
    if (saved) {
      closeForm();
    }
    return saved;
  };

  const submitUpdate = async (
    tierId: string,
    form: Parameters<typeof handleUpdate>[1],
  ): Promise<boolean> => {
    const saved = await handleUpdate(tierId, form);
    if (saved) {
      closeForm();
    }
    return saved;
  };

  const renderList = () => {
    if (isLoading) {
      return <TableSkeleton />;
    }
    if (error && !tiers.length) {
      return (
        <p className="py-10 text-center text-red-500">
          Failed to load affiliate tiers. Try reloading the page.
        </p>
      );
    }
    if (!tiers.length) {
      return (
        <p className="py-10 text-center text-gray-500 dark:text-gray-400">
          No affiliate tiers yet.
        </p>
      );
    }
    return (
      <>
        {error ? (
          <p className="pb-4 text-red-500" data-testid="affiliate-tiers-stale">
            Failed to refresh affiliate tiers, so the list below may be out of
            date. Try reloading the page.
          </p>
        ) : null}
        <AffiliateTierTable
          items={tiers}
          isSaving={isSaving}
          onEdit={openForm}
          onMakeDefault={setMakingDefault}
        />
      </>
    );
  };

  return (
    <div className="w-full">
      <PermissionsProvider
        permission={AfriexAdminPermission.BusinessAffiliateManage}
      >
        <div className="flex justify-end">
          <Button
            colorScheme="cyan"
            onClick={() => openForm()}
            data-testid="affiliate-tier-create"
          >
            Create tier
          </Button>
        </div>
      </PermissionsProvider>

      <section className="pt-5">{renderList()}</section>

      {isFormOpen ? (
        <AffiliateTierFormModal
          isOpen={isFormOpen}
          onClose={closeForm}
          isSaving={isSaving}
          errors={errors}
          tier={editing}
          currentDefault={currentDefault}
          validateCreate={validateCreate}
          validateUpdate={validateUpdate}
          onCreate={submitCreate}
          onUpdate={submitUpdate}
        />
      ) : null}

      <ConfirmTierChangeModal
        isOpen={Boolean(makingDefault)}
        title="Make default tier"
        confirmLabel="Make default"
        loadingText="Saving..."
        onClose={() => setMakingDefault(undefined)}
        onConfirm={async () => {
          if (!makingDefault) {
            return false;
          }
          try {
            return await makeDefault(makingDefault.id);
          } catch (e) {
            logRejection('Failed to set the default affiliate tier')(e);
            return false;
          }
        }}
      >
        <p>
          New referrals will be captured under{' '}
          <span className="font-medium">{makingDefault?.name}</span>
          {currentDefault ? ` instead of ${currentDefault.name}` : ''}.
        </p>
        {makingDefault &&
        currentDefault &&
        makingDefault.termsVersion !== currentDefault.termsVersion ? (
          <p data-testid="affiliate-tier-terms-warning">
            Its terms version ({makingDefault.termsVersion}) differs from the
            current one ({currentDefault.termsVersion}), so every referrer is
            asked to accept again and payouts pause until they do.
          </p>
        ) : null}
      </ConfirmTierChangeModal>
    </div>
  );
};
